import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, HeartPulse, Lock, Server, EyeOff, Activity } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const CARDS = [
  {
    Icon: ShieldCheck,
    title: "SOC 2",
    body: "Type I in place today, with Type II underway — independently audited controls for security and availability.",
    badge: "Type II · in progress",
  },
  {
    Icon: HeartPulse,
    title: "HIPAA-aligned",
    body: "Handling of protected health information follows HIPAA safeguards — essential for PI and medical intake.",
    badge: "PHI-safe workflows",
  },
  {
    Icon: Lock,
    title: "Encrypted end-to-end",
    body: "Data is encrypted in transit and at rest, so call content and case details are protected at every hop.",
    badge: "In transit + at rest",
  },
  {
    Icon: Server,
    title: "US-hosted",
    body: "Infrastructure hosted in the United States, on reputable cloud providers with hardened access controls.",
    badge: "US data residency",
  },
  {
    Icon: EyeOff,
    title: "Never trains shared models",
    body: "Your conversations and case data are never used to train models shared with other firms. Your data stays yours.",
    badge: "No shared training",
  },
  {
    Icon: Activity,
    title: "Reliable & watched",
    body: "99% uptime with 24/7 monitoring and automatic failover, so the line is answered even when something breaks.",
    badge: "99% uptime · 24/7",
  },
];

export const SecuritySlide = () => {
  const reduce = useReducedMotion();

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
  };
  const cardsWrap = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.09 } },
  };
  const cardItem = {
    hidden: { opacity: 0, y: reduce ? 0 : 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
  };

  return (
    <Stage testId="appendix-security">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(18px,3.6%,40px)]"
      >
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Appendix · Security &amp; data</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-accent tabular-nums">
            A · 2 of 3
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Built for firms that can't afford a data mistake.
        </motion.h1>
        <motion.p variants={item} className="font-sans text-[14px] text-ink-soft mt-2 max-w-[72ch]">
          Client confidentiality is the job. Lexafide is engineered so your data
          stays yours — protected, private, and under your control.
        </motion.p>

        <motion.div
          variants={cardsWrap}
          className="flex-1 min-h-0 grid grid-cols-1 min-[560px]:grid-cols-2 min-[900px]:grid-cols-3 gap-3.5 mt-5"
        >
          {CARDS.map((c, i) => (
            <motion.div
              key={i}
              variants={cardItem}
              data-testid={`security-card-${i + 1}`}
              className="rounded-2xl border border-hairline bg-[#FBFAF6] p-4 flex flex-col"
            >
              <span className="w-9 h-9 rounded-lg bg-accent/12 flex items-center justify-center">
                <c.Icon className="w-4 h-4 text-accent" />
              </span>
              <h3 className="font-display font-medium text-base text-ink mt-3">
                {c.title}
              </h3>
              <p className="font-sans text-[12.5px] text-ink-soft leading-snug mt-1.5 flex-1">
                {c.body}
              </p>
              <span className="inline-flex self-start items-center font-mono text-[9px] uppercase tracking-[0.1em] text-accent border border-accent/30 rounded-full px-2 py-0.5 mt-3">
                {c.badge}
              </span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={item}
          className="border-t border-hairline pt-4 mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
        >
          <p className="font-sans text-[12px] text-ink-soft">
            <span className="font-medium text-ink">You stay in control</span> ·
            Confidence thresholds · human override · one-click rollback
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-muted">
            Full security documentation available on request.
          </p>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default SecuritySlide;
